
import store from '@/store';
import Vue from 'vue';

export default {
  name: 'MemoBadgesLayer',
  props: {
    plusIndex: Number,
    dates: Array,
    positions: Array,
    memosByDate: Object
  },
  data() {
    return {
      wrappers: [],
      previewLimit: 2,
      expandedDates: [],
      viewAllDialog: false,
      selectedDate: null,
      localMemosByDate: {} // will copy from props in mounted
    };
  },
  computed: {
    // All memos for selectedDate, grouped across hours
    selectedMemos() {
      if (!this.selectedDate) return [];

      // get all keys that start with the selectedDate (ignore hour format)
      const keys = Object.keys(this.localMemosByDate).filter(key =>
        key.startsWith(this.selectedDate)
      );

      // flatten all memos for this day
      return keys.reduce(
        (arr, key) => arr.concat(this.localMemosByDate[key]),
        []
      );
    }
  },
  watch: {
    memosByDate: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.localMemosByDate = { ...newVal };
      }
    }
  },
  methods: {
    canEditMemo(memo) {
      return !!memo?.canEdit;
    },

    canDeleteMemo(memo) {
      const currentUserId = this.$store.state?.user?.profile?._id || '';
      const isMemoAdmin = !!this.$store.state?.user?.role?.isAdmin;
      const isCreator =
        !!memo?.userId && !!currentUserId && memo.userId === currentUserId;

      return isCreator || isMemoAdmin;
    },

    getKey(item) {
      let hourStr = item.hour;
      if (typeof hourStr === 'number') {
        const h = Math.floor(hourStr / 100)
          .toString()
          .padStart(2, '0');
        const m = (hourStr % 100).toString().padStart(2, '0');
        hourStr = `${h}:${m}`;
      }
      if (!hourStr) hourStr = '12:00';
      return item?.key || `${item.date}_${hourStr}`;
    },

    memoCount(item) {
      const key = this.getKey(item);
      return this.localMemosByDate[key]?.length || 0;
    },

    previewMemos(item) {
      const key = this.getKey(item);
      const list = this.localMemosByDate[key] || [];
      return this.expandedDates.includes(key)
        ? list
        : list.slice(0, this.previewLimit);
    },

    handleClick(item) {
      if (this.memoCount(item) === 0) {
        this.addMemo(item);
      } else {
        this.openViewAll(item); // pass only date
      }
    },

    openViewAll(item) {
      this.selectedDate = item.date;
      this.selectedHour = null; // show all memos for the date
      this.viewAllDialog = true;
       Vue.prototype?.$mixpanel?.track('View All memo', {
          referrer: document.referrer
        });
    },

    addMemo(item) {
      const key = this.getKey(item);
      if (!this.localMemosByDate[key]) this.localMemosByDate[key] = [];

      // emit to parent
      this.$emit('add', { date: item.date, hour: item.hour || '12:00' });
     Vue.prototype?.$mixpanel?.track('Added memo', {
          referrer: document.referrer
        });
      // optional: add a temporary placeholder to show instantly
      // this.localMemosByDate[key].push({ id: Date.now(), text: 'New memo', ...item });
    },

    deleteMemo(memo) {
      // Find the exact key in localMemosByDate
      const key = Object.keys(this.localMemosByDate).find(k => {
        const parts = k.split('_');
        const date = parts[0];
        const hour = parts[1];
        // compare date & hour
        const memoHour =
          String(Math.floor(memo.startHour / 100)).padStart(2, '0') +
          ':' +
          String(memo.startHour % 100).padStart(2, '0');
        return date === memo.startDate && hour === memoHour;
      });

      if (key && this.localMemosByDate[key]) {
        // Remove the memo
        this.localMemosByDate[key] = this.localMemosByDate[key].filter(
          m => m.id !== memo.id
        );

        // If no memos left for this key, delete the key
        if (this.localMemosByDate[key].length === 0) {
          delete this.localMemosByDate[key];
        }
      }

      // emit to parent
      this.$emit('delete', {
        id: memo.id,
        storeId: memo.storeId,
        date: memo.startDate,
        hour: memo.startHour
      });
    },

    getMemoAuthorName(memo) {
      const profile = this.$store.state?.user?.profile || {};
      const name =
        memo?.authorName || memo?.userName || memo?.username || memo?.fullName;

      if (typeof name === 'string' && name.trim()) {
        return name.trim();
      }

      if (this.getMemoUserId(memo) && this.getMemoUserId(memo) === profile?._id) {
        return this.getProfileDisplayName(profile);
      }

      return 'User';
    },

    getMemoUserId(memo) {
      return memo?.userId || memo?.author || memo?.user?._id || memo?.user?.id;
    },

    getProfileDisplayName(profile) {
      const firstLast = [profile?.firstName, profile?.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();

      return (
        profile?.fullName ||
        firstLast ||
        profile?.username ||
        profile?.email ||
        'User'
      );
    },

    formatDateTime(authorName, date, hour) {
      if (!date || !hour) return authorName || 'User';
      const str = date.toString();
      const formattedDate = `${str.slice(0, 4)}/${str.slice(4, 6)}/${str.slice(
        6,
        8
      )}`;
      const h = String(Math.floor(hour / 100)).padStart(2, '0');
      const m = String(hour % 100).padStart(2, '0');
      const locale = (
        this.$store.getters['user/getLocale'] || 'en'
      ).toLowerCase();
      const name = authorName || 'User';

      return locale.includes('ja')
        ? `${name}, ${formattedDate} ${h}:${m}`
        : `${name}, ${formattedDate} at ${h}:${m}`;
    },

    truncate(text, max) {
      return text?.length > max ? text.slice(0, max) + '…' : text;
    },

    getPrimaryTagLabel(tags = []) {
      const tagKey = tags[0];
      if (!tagKey) return '';
      return this.$store.state.user.translate[tagKey] || tagKey;
    },

    getStoreNameById(id) {
      const storeList = store.getters['user/getStoresCodeNameList'] || [];
      const storeItem = storeList.find(s => s.id === id);
      return storeItem ? `${storeItem.storeCode} - ${storeItem.name}` : id;
    },

    getPopoverStyle(index) {
      const wrapper = this.wrappers[index];
      if (!wrapper) return {};
      const rect = wrapper.getBoundingClientRect();
      const popoverWidth = 300;
      const screenWidth = window.innerWidth;
      let left = '50%';
      let transform = 'translateX(-50%)';
      if (rect.left + popoverWidth / 2 > screenWidth)
        transform = `translateX(${screenWidth - rect.right - popoverWidth}px)`;
      else if (rect.left - popoverWidth / 2 < 0)
        transform = `translateX(${popoverWidth / 3 - rect.left}px)`;
      return { left, transform };
    }
  }
};
